// Doc Order — read-model builders. Assemble the loan facts the gates/derives
// read and the display extras the Snapshot renders from the order snapshot +
// the authoritative ProspectLoan row. No business rules here — just projection.
import type { ProspectLoan } from '../types';
import type { DocOrderRow } from '@/services/docOrderService';
import { lockRateValue } from '@/services/lockNetBuyRate';
import { assemblePropertyAddress, normalizeEncompassDate, type DocOrderLoanFacts } from './core';

function num(v: unknown): number | null {
  const n = Number(v);
  return Number.isFinite(n) && (v !== null && v !== undefined) && v !== '' ? n : null;
}

// Full subject-property address on ONE line (Jennifer bug #1; R4 #14 — the
// implementation now lives in doc-order-core so the server PDF/derive and this
// client share ONE assembler).
function assembleProperty(loan: ProspectLoan): string | null {
  return assemblePropertyAddress({
    street: loan.property_address,
    city: loan.subject_property_city,
    state: loan.subject_property_state,
    zip: loan.property_zip
  });
}

export function buildBorrowers(loan: ProspectLoan): string[] {
  const primary = [loan.borrower_first_name, loan.borrower_last_name].filter(Boolean).join(' ').trim();
  return primary ? [primary] : [];
}

/** Facts read by the client-side gates + Snapshot (subset of the pulled snapshot,
 *  overlaid on the authoritative loan row). */
export function buildLoanFacts(order: DocOrderRow | null, loan: ProspectLoan): DocOrderLoanFacts {
  const pulled = order?.encompass_snapshot?.pulled;
  return {
    loanNumber: loan.trans_details_loan_number ?? '',
    borrowers: buildBorrowers(loan),
    property: assembleProperty(loan),
    propertyState: pulled?.propertyState ?? loan.subject_property_state ?? null,
    propertyType: loan.property_type ?? null,
    product: loan.rate_lock_loan_program ?? null,
    // R4 #15: Field 1401 (loan program) — the DSCR SIGNAL; snapshot-pulled.
    loanProgram1401: (pulled?.loanProgram1401 as string | undefined) ?? null,
    purpose: loan.trans_details_loan_purpose ?? null,
    loanAmount: num(pulled?.totalLoanAmount) ?? num(loan.trans_details_loan_amt) ?? 0,
    baseLoanAmount: num(pulled?.baseLoanAmount),
    channel: loan.channel ?? null,
    // R5: same read-boundary normalization as the edge — legacy snapshots
    // can hold "//"-class placeholders; the client gates must not see them.
    initialCdSentDate: normalizeEncompassDate(pulled?.initialCdSentDate),
    cdReceivedDate: normalizeEncompassDate(pulled?.cdReceivedDate),
    gfeDate: normalizeEncompassDate(pulled?.gfeDate),
    piwField4954: !!pulled?.piw,
    // Encompass 762 (rateLock.buySideLockExpires) — webhook-synced on the loan row.
    lockExpiration: loan.rate_lock_buy_side_lock_expires ?? null,
    reservesLock: null, // locked-reserves source + formula TBA (Jennifer bug 1)
    reservesFile: num(loan.months_reserves),
    cashToFromBorrower: num(pulled?.cashToFromBorrower),
    occupancy: pulled?.occupancy ?? loan.occupancy_status ?? null
  };
}

/** Display-only fields the embedded Snapshot shows beyond the gate facts. Fields
 *  absent from the loan row render as an em dash — never fabricated. */
export interface SnapshotExtras {
  rate: number | null;
  ltv: number | null;
  cltv: number | null;
  housingRatio: number | null;
  dtiRatio: number | null;
  /** CX.RENT.DSCR mirror (webhook-synced) — displayed as a ratio, e.g. "1.25x". */
  dscrRatio: number | null;
  occupancy: string | null;
  lienPosition: string | null;
  loanOfficer: string | null;
  processor: string | null;
  impoundsWaived: string | null;
}

export function buildSnapshotExtras(loan: ProspectLoan): SnapshotExtras {
  return {
    rate: num(lockRateValue(loan)) ?? num(loan.trans_details_interest_rate),
    ltv: num(loan.rate_lock_ltv) ?? num(loan.freddie_mac_ltv_ratio_pct),
    cltv: num(loan.rate_lock_cltv) ?? num(loan.trans_details_cltv_ratio_pct) ?? num(loan.hcltv_ratio),
    housingRatio: num(loan.housing_expense_ratio),
    dtiRatio: num(loan.dti_ratio),
    dscrRatio: num(loan.dscr_ratio),
    occupancy: loan.occupancy_status ?? null,
    lienPosition: loan.lien_position ?? null,
    loanOfficer: [loan.loan_officer_first_name, loan.loan_officer_last_name].filter(Boolean).join(' ') || null,
    processor: [loan.loan_processor_first_name, loan.loan_processor_last_name].filter(Boolean).join(' ') || null,
    impoundsWaived: loan.rate_lock_impound_waived ?? null
  };
}
